import { useId, type ReactNode } from "react";

import { COARSE_HIT_AREA, FOCUS_RING, cx } from "./controlStyles";
import { InfoTip } from "./InfoTip";

// On/off setting that applies immediately (role="switch"). The label (and
// description) sit to the left; clicking them toggles too. For a choice
// that is only applied on Save, use a Checkbox instead.
//
//   <Switch checked={on} onChange={setOn} label="Reply without @"
//     description="Answers every message in the group." />
//
// `hint` puts the explanation in an (i) beside the label instead of a line
// under it; the switch is still named by the label alone.

interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  /** A tooltip on an (i) beside the label. */
  hint?: string;
  disabled?: boolean;
  /** Keep the label for screen readers only (e.g. in a table row). */
  hideLabel?: boolean;
  id?: string;
  className?: string;
}

export function Switch({
  checked,
  onChange,
  label,
  description,
  hint,
  disabled,
  hideLabel,
  id,
  className,
}: SwitchProps) {
  const auto = useId();
  const switchId = id ?? `${auto}-switch`;
  const labelId = `${auto}-label`;
  const descId = description ? `${auto}-desc` : undefined;
  return (
    <div className={cx("flex items-start justify-between gap-4", disabled && "opacity-45", className)}>
      <label
        htmlFor={switchId}
        className={cx("min-w-0 flex-1", hideLabel ? "sr-only" : "cursor-pointer", disabled && "cursor-default")}
      >
        {hint ? (
          <span className="flex items-center gap-1.5">
            <span id={labelId} className="text-sm font-medium text-fg">
              {label}
            </span>
            <InfoTip text={hint} />
          </span>
        ) : (
          <span id={labelId} className="block text-sm font-medium text-fg">
            {label}
          </span>
        )}
        {description && (
          <span id={descId} className="mt-0.5 block text-xs text-fg-muted">
            {description}
          </span>
        )}
      </label>
      <button
        id={switchId}
        type="button"
        role="switch"
        aria-checked={checked}
        // Named by the label text alone: the <label> also wraps the
        // description, which is announced once, as the description.
        aria-labelledby={labelId}
        aria-describedby={descId}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cx(
          "relative mt-0.5 inline-flex h-[20px] w-[36px] shrink-0 items-center rounded-pill p-[3px] transition-colors duration-150 disabled:cursor-default",
          checked
            ? "bg-accent-500"
            : "bg-[color:var(--c-border-strong)] shadow-[inset_0_0_0_1px_var(--c-control-edge)]",
          FOCUS_RING,
          COARSE_HIT_AREA,
        )}
      >
        <span
          aria-hidden
          className={cx(
            "h-[14px] w-[14px] rounded-full transition-transform duration-150",
            checked ? "translate-x-[16px] bg-[color:var(--c-on-accent)]" : "translate-x-0 bg-[color:var(--c-text-primary)]",
          )}
        />
      </button>
    </div>
  );
}
